import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import { Link as RouterLink } from "@tanstack/react-router";
import { TIER_LABELS } from "@/lib/quotas";
import type { QuotaState } from "@/hooks/useQuota";

type Props = {
  open: boolean;
  onClose: () => void;
  quota: QuotaState;
  /** Optional message to override the default quota-hit message. */
  message?: string;
};

export function QuotaUpgradeDialog({ open, onClose, quota, message }: Props) {
  const upgradeLabel = quota.upgradeTier ? TIER_LABELS[quota.upgradeTier] : null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>You've hit your monthly limit</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {message ??
              `Your ${quota.tierLabel} plan includes ${quota.formattedLimit} ${quota.label.toLowerCase()} per month, and you've used all of them.`}
          </Typography>
          <Stack spacing={0.5}>
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {quota.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {quota.used} / {quota.formattedLimit}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, quota.percent)}
              color={quota.exceeded ? "error" : "primary"}
            />
          </Stack>
          {upgradeLabel ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Chip size="small" color="primary" label={`Upgrade to ${upgradeLabel}`} />
              <Typography variant="caption" color="text.secondary">
                for more headroom
              </Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Your usage resets on the 1st of next month (UTC).
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Maybe later</Button>
        {quota.upgradeTier && (
          <Button
            variant="contained"
            component={RouterLink}
            to="/pricing"
            onClick={onClose}
          >
            See plans
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
